/**
 * PageViewTracker — cookieless first-party page-view beacon.
 *
 * Fires on every SPA route change to POST /api/te (Cloudflare Pages worker),
 * which stamps connect-from geo (request.cf country/city/region) and stores
 * the hit via the log_page_view RPC. No cookies, no IP, no persistent visitor
 * id: sessions are a random per-tab id in sessionStorage. Authenticated users
 * include their user id (first-party account analytics), which is what lets
 * the admin dashboard separate "website" traffic from registered members.
 *
 * Admin pages are not tracked. UTM params are only sent on the entry page
 * (they identify the session's source; interior navigations carry none).
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const SESSION_KEY = "te_sid";

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // Storage blocked — still track the hit, just without session stitching.
    return "no-session";
  }
}

function sendPageView(path: string, search: string, isFirstHit: boolean) {
  if (path.startsWith("/admin")) return;

  const params = new URLSearchParams(search);
  const payload: Record<string, string> = {
    s: getSessionId(),
    p: path,
    l: document.documentElement.lang || "en",
  };

  if (isFirstHit) {
    if (document.referrer) payload.r = document.referrer;
    const us = params.get("utm_source");
    const um = params.get("utm_medium");
    const uc = params.get("utm_campaign");
    if (us) payload.us = us;
    if (um) payload.um = um;
    if (uc) payload.uc = uc;
  }

  try {
    const uid = sessionStorage.getItem("te_user_id");
    if (uid) payload.u = uid;
  } catch { /* ignore */ }

  const body = JSON.stringify(payload);
  try {
    if (!navigator.sendBeacon || !navigator.sendBeacon("/api/te", body)) {
      void fetch("/api/te", { method: "POST", body, keepalive: true }).catch(() => {});
    }
  } catch { /* analytics must never break navigation */ }
}

export default function PageViewTracker() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    // Route changes that only touch query/hash aren't new page views.
    if (lastPath.current === location.pathname) return;
    const isFirstHit = lastPath.current === null;
    lastPath.current = location.pathname;
    sendPageView(location.pathname, location.search, isFirstHit);
  }, [location.pathname, location.search]);

  return null;
}
